import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LegalApi from "../utils/legalApi";

const PrivacyPolicy = () => {
  const { getCurrentPrivacyPolicy } = LegalApi();

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getCurrentPrivacyPolicy();

      const document =
        response?.data?.document ??
        response?.data ??
        response?.document ??
        response;

      if (!document?.content) {
        throw new Error("Current Privacy Policy is unavailable.");
      }

      setPolicy(document);
    } catch (error) {
      console.error("Fetch Privacy Policy error:", error);

      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load the Privacy Policy.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
    // eslint-disable-next-line 
  }, []);

  const updatedAt = policy?.effectiveAt || policy?.publishedAt;

  const formattedDate = updatedAt
    ? new Date(updatedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      })
    : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f7f9fc",
        py: { xs: 4, md: 7 },
      }}
    >
      <Container maxWidth="md">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 5 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1D629D" }}>
            PristinePV
          </Typography>

          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              color: "#1D629D",
              fontWeight: 600,
            }}
          >
            Back to website
          </Button>
        </Stack>

        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e3e8ef",
            borderRadius: 3,
            p: { xs: 3, md: 5 },
          }}
        >
          {loading ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 10 }}>
              <CircularProgress sx={{ color: "#1D629D" }} />
              <Typography color="text.secondary">
                Loading Privacy Policy...
              </Typography>
            </Stack>
          ) : error ? (
            <Stack spacing={2}>
              <Alert severity="error">{error}</Alert>

              <Button
                variant="outlined"
                onClick={fetchPolicy}
                sx={{ alignSelf: "flex-start" }}
              >
                Try again
              </Button>
            </Stack>
          ) : (
            <>
              <Typography
                variant="h3"
                sx={{
                  color: "#1D629D",
                  fontWeight: 700,
                  mb: 1,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                {policy.title || "Privacy Policy"}
              </Typography>

              <Typography variant="body2" sx={{ color: "#667382", mb: 4 }}>
                {formattedDate && `Effective date: ${formattedDate}`}
                {policy.version && ` · Version ${policy.version}`}
              </Typography>

              <Box
                sx={{
                  color: "#4b5563",
                  fontSize: "0.95rem",
                  lineHeight: 1.8,
                  overflowWrap: "anywhere",

                  /* Main section headings */
                  "& h1": {
                    color: "#1D629D",
                    fontSize: { xs: "1.6rem", md: "1.85rem" },
                    fontWeight: 800,
                    lineHeight: 1.4,
                    mt: 4,
                    mb: 2,
                    pb: 1.5,
                    borderBottom: "2px solid #d8eaf4",
                  },

                  /* Numbered section headings */
                  "& h2": {
                    color: "#273444",
                    fontSize: { xs: "1.25rem", md: "1.4rem" },
                    fontWeight: 800,
                    lineHeight: 1.5,
                    mt: 4,
                    mb: 1.5,
                    pb: 1,
                    borderBottom: "1px solid #e3e8ef",
                  },

                  /* Subheadings */
                  "& h3": {
                    color: "#273444",
                    fontSize: "1.08rem",
                    fontWeight: 800,
                    lineHeight: 1.5,
                    mt: 2.5,
                    mb: 1,
                  },

                  "& h4, & h5, & h6": {
                    color: "#273444",
                    fontWeight: 800,
                    mt: 2,
                    mb: 1,
                  },

                  "& p": { mb: 2 },
                  "& ul, & ol": { pl: 3, mb: 2 },
                  "& li": { mb: 0.8 },

                  "& a": {
                    color: "#1D629D",
                    textDecoration: "none",
                  },

                  "& a:hover": {
                    textDecoration: "underline",
                  },

                  "& table": {
                    display: "block",
                    width: "100%",
                    overflowX: "auto",
                    borderCollapse: "collapse",
                    mb: 2,
                  },

                  "& th, & td": {
                    border: "1px solid #e3e8ef",
                    padding: 1.5,
                    textAlign: "left",
                  },

                  "& th": {
                    backgroundColor: "#f7f9fc",
                    color: "#273444",
                  },
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children, ...props }) => {
                      const isExternal =
                        href?.startsWith("https://") ||
                        href?.startsWith("http://");

                      return (
                        <a
                          href={href}
                          {...props}
                          {...(isExternal
                            ? {
                                target: "_blank",
                                rel: "noopener noreferrer",
                              }
                            : {})}
                        >
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {policy.content}
                </ReactMarkdown>
              </Box>
            </>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="center"
            alignItems="center"
            sx={{
              mt: 5,
              pt: 3,
              borderTop: "1px solid #e3e8ef",
            }}
          >
           

            <Button
              component={Link}
              to="/terms-and-conditions"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Terms & Conditions
            </Button>

            <Button
              component={Link}
              to="/delete-account"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Delete Account
            </Button>

            <Button
              component={Link}
              to="/recover-account"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Recover Account
            </Button>
          </Stack>
        </Box>

        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 4, color: "#7a8594" }}
        >
          © {new Date().getFullYear()} Pristine PV Limited. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
